import { RESERVE_STORE, runRequest } from './offline-db';

/**
 * Phrases fetched ahead while online, per account, task and language, so a
 * session can go on offline. Each record is one chunk of a task's list; the
 * task decides what a chunk is (a page of 100 for "Enregistrer", the whole
 * shuffled reserve for "Traduire").
 */

export type ReserveTask = 'speak' | 'translate';

export type ReserveRecord<T = unknown> = {
  key: string;
  ownerId: string;
  task: ReserveTask;
  lang: string;
  chunk: number;
  items: T[];
  savedAt: number;
};

/** Older than this, a chunk is fetched again when online (phrases get edited or removed). */
export const RESERVE_MAX_AGE_MS = 12 * 60 * 60_000;

export const reserveKey = (ownerId: string, task: ReserveTask, lang: string, chunk: number) =>
  `${ownerId}|${task}|${lang}|${chunk}`;

export const isReserveFresh = (record: ReserveRecord | undefined, now = Date.now()) =>
  Boolean(record) && now - record.savedAt < RESERVE_MAX_AGE_MS;

export const phraseReserve = {
  async get<T>(ownerId: string, task: ReserveTask, lang: string, chunk: number): Promise<ReserveRecord<T> | undefined> {
    const record = await runRequest<ReserveRecord<T> | undefined>(RESERVE_STORE, 'readonly', (store) =>
      store.get(reserveKey(String(ownerId), task, lang, chunk)),
    );
    return record && record.ownerId === String(ownerId) ? record : undefined;
  },

  async save<T>(ownerId: string, task: ReserveTask, lang: string, chunk: number, items: T[], savedAt = Date.now()): Promise<void> {
    const record: ReserveRecord<T> = {
      key: reserveKey(String(ownerId), task, lang, chunk),
      ownerId: String(ownerId),
      task,
      lang,
      chunk,
      items,
      savedAt,
    };
    await runRequest(RESERVE_STORE, 'readwrite', (store) => store.put(record));
  },

  async chunks(ownerId: string, task: ReserveTask, lang: string): Promise<ReserveRecord[]> {
    const records = await runRequest<ReserveRecord[]>(RESERVE_STORE, 'readonly', (store) =>
      store.index('ownerId').getAll(IDBKeyRange.only(String(ownerId))),
    );
    return records.filter((record) => record.task === task && record.lang === lang).sort((a, b) => a.chunk - b.chunk);
  },

  async remove(ownerId: string, task: ReserveTask, lang: string, chunk: number): Promise<void> {
    await runRequest(RESERVE_STORE, 'readwrite', (store) => store.delete(reserveKey(String(ownerId), task, lang, chunk)));
  },
};
